import { Grid } from '../../core/Grid.js';
import {
    Position,
    Direction,
    positionKey,
    positionsEqual,
    oppositeDir
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';
import { stripSeparators } from '../stateSerialization.js';

// All four cardinal directions
const ALL_DIRECTIONS: Direction[] = ['N', 'E', 'S', 'W'];

export interface Antenna {
    id: number;
    path: Position[];  // Cell centers forming the antenna path
    signalDirection: Direction;  // Direction the signal goes from second end
}

export interface Signal {
    from: Position;           // Antenna's second end
    direction: Direction;     // Direction it travels
    crossedDigit: number | null;
    targetAntennaId: number | null;  // Which antenna this signal reaches (null if blocked)
    blocked: boolean;         // True if signal is blocked by antenna body
    collisionPosition: Position | null;  // Cell where the signal stopped (receiver or body), null if it loops
}

export type AntennaColor = 'teal' | 'amber' | 'red';

export interface BadgeInfo {
    digit: number;          // Digit value to display
    color: AntennaColor;
}

export interface SignalDigitCrossing {
    position: Position;
    digit: number;
    direction: Direction;  // Direction signal travels through this cell
}

export class AntennasGame {
    private grid: Grid;
    private antennas: Map<number, Antenna> = new Map();
    private nextAntennaId: number = 1;
    private currentPath: Position[] = [];
    private digitPositions: Map<number, Position> = new Map();

    protected get gs() { return this.grid.serializer; }

    constructor(grid: Grid) {
        this.grid = grid;
        this.initializeDigitPositions();
    }

    private initializeDigitPositions(): void {
        const positions = this.grid.getAllDigitPositions();
        for (const [digit, pos] of positions) {
            this.digitPositions.set(digit, pos);
        }
    }

    // Wrapping movement for toroidal signal propagation
    protected wrapMove(pos: Position, dir: Direction): Position {
        const deltas: Record<Direction, {dr: number, dc: number}> = {
            'N': {dr: -1, dc: 0}, 'S': {dr: 1, dc: 0},
            'E': {dr: 0, dc: 1}, 'W': {dr: 0, dc: -1}
        };
        const d = deltas[dir];
        const size = this.grid.size;
        return {
            row: (pos.row + d.dr + size) % size,
            col: (pos.col + d.dc + size) % size
        };
    }

    /** Hook for vortex teleportation. Returns exit gate position, or null if no teleport. */
    protected checkVortexTeleport(_position: Position): Position | null {
        return null;
    }

    getGrid(): Grid {
        return this.grid;
    }

    getAntennas(): Map<number, Antenna> {
        return this.antennas;
    }

    isStarted(): boolean {
        return this.antennas.size > 0;
    }

    getCurrentPath(): Position[] {
        return this.currentPath;
    }

    getDigitPositions(): Map<number, Position> {
        return this.digitPositions;
    }

    // Get the second end (signal emitting end) of an antenna
    getSignalEnd(antenna: Antenna): Position {
        return antenna.path[antenna.path.length - 1];
    }

    // Get the first end of an antenna
    getFirstEnd(antenna: Antenna): Position {
        return antenna.path[0];
    }

    // Get direction from second-to-last cell to last cell (antenna's exit direction)
    private getAntennaExitDirection(antenna: Antenna): Direction {
        if (antenna.path.length < 2) return 'N';
        const last = antenna.path[antenna.path.length - 1];
        const prev = antenna.path[antenna.path.length - 2];

        if (prev.row < last.row) return 'S';  // came from up, exits down
        if (prev.row > last.row) return 'N';  // came from down, exits up
        if (prev.col < last.col) return 'E';  // came from left, exits right
        return 'W';  // came from right, exits left
    }

    // Get the blocked direction (where antenna body is)
    private getBlockedDirection(antenna: Antenna): Direction {
        const exitDir = this.getAntennaExitDirection(antenna);
        return oppositeDir(exitDir);  // Blocked is opposite of exit
    }

    // Get valid signal directions for an antenna (3 directions, excluding blocked)
    getValidSignalDirections(antenna: Antenna): Direction[] {
        const blocked = this.getBlockedDirection(antenna);
        return ALL_DIRECTIONS.filter(d => d !== blocked);
    }

    /** Aim an antenna's signal at `dir`, if that direction isn't blocked by its own body. */
    setSignalDirection(antennaId: number, dir: Direction): boolean {
        const antenna = this.antennas.get(antennaId);
        if (!antenna) return false;
        if (!this.getValidSignalDirections(antenna).includes(dir)) return false;
        if (antenna.signalDirection === dir) return false;
        antenna.signalDirection = dir;
        return true;
    }

    // Cycle to next valid signal direction
    cycleSignalDirection(antennaId: number): boolean {
        const antenna = this.antennas.get(antennaId);
        if (!antenna) return false;

        const validDirs = this.getValidSignalDirections(antenna);
        const currentIndex = validDirs.indexOf(antenna.signalDirection);
        const nextIndex = (currentIndex + 1) % validDirs.length;
        antenna.signalDirection = validDirs[nextIndex];
        return true;
    }

    /** Whether an antenna cell may occupy `pos`: on the grid, not a digit, and not
     *  already part of a finished antenna. (A build in progress doesn't block —
     *  that's checked against `currentPath` separately.) */
    canBuildOn(pos: Position): boolean {
        return this.isValidAntennaCell(pos);
    }

    // Check if a cell is valid for antenna path
    protected isValidAntennaCell(pos: Position): boolean {
        if (!this.grid.isValidPosition(pos)) {
            return false;
        }

        // Can't place on digit cells
        for (const [, digitPos] of this.digitPositions) {
            if (positionsEqual(pos, digitPos)) {
                return false;
            }
        }

        // Can't overlap with existing antennas
        for (const [, antenna] of this.antennas) {
            for (const cell of antenna.path) {
                if (positionsEqual(pos, cell)) {
                    return false;
                }
            }
        }

        return true;
    }

    // Check if position is in same row or column as another
    private isAligned(a: Position, b: Position): boolean {
        return a.row === b.row || a.col === b.col;
    }

    // Get cells between two aligned positions (exclusive)
    private getCellsBetween(a: Position, b: Position): Position[] {
        const cells: Position[] = [];
        if (a.row === b.row) {
            const minCol = Math.min(a.col, b.col);
            const maxCol = Math.max(a.col, b.col);
            for (let col = minCol + 1; col < maxCol; col++) {
                cells.push({ row: a.row, col });
            }
        } else if (a.col === b.col) {
            const minRow = Math.min(a.row, b.row);
            const maxRow = Math.max(a.row, b.row);
            for (let row = minRow + 1; row < maxRow; row++) {
                cells.push({ row, col: a.col });
            }
        }
        return cells;
    }

    startAntenna(pos: Position): boolean {
        if (!this.isValidAntennaCell(pos)) return false;
        if (this.currentPath.length > 0) return false;

        this.currentPath = [{ ...pos }];
        return true;
    }

    extendAntenna(pos: Position): boolean {
        if (this.currentPath.length === 0) return false;

        const last = this.currentPath[this.currentPath.length - 1];

        if (!this.isAligned(last, pos)) return false;
        if (positionsEqual(last, pos)) return false;

        const cellsBetween = this.getCellsBetween(last, pos);
        for (const cell of cellsBetween) {
            if (!this.isValidAntennaCell(cell)) return false;
            if (this.currentPath.some(p => positionsEqual(p, cell))) return false;
        }

        if (!this.isValidAntennaCell(pos)) return false;
        if (this.currentPath.some(p => positionsEqual(p, pos))) return false;

        for (const cell of cellsBetween) {
            this.currentPath.push({ ...cell });
        }
        this.currentPath.push({ ...pos });

        return true;
    }

    finishAntenna(): boolean {
        if (this.currentPath.length < 2) return false;

        const antenna: Antenna = {
            id: this.nextAntennaId,
            path: [...this.currentPath],
            signalDirection: 'N'  // Provisional; aimed below.
        };
        antenna.signalDirection = this.getValidSignalDirections(antenna)[0];

        this.antennas.set(antenna.id, antenna);
        this.nextAntennaId++;
        this.currentPath = [];

        // Aim only now that it's on the board: the incoming signal that says which
        // digit to head for doesn't exist until this antenna is there to receive it.
        antenna.signalDirection = this.bestSignalDirection(antenna);
        return true;
    }

    /**
     * The direction to open a freshly placed emitter on. When the antenna already
     * receives a signal carrying a digit, aim at the **next** digit up if a clear
     * line reaches it — shortest line first, then preferring one that doesn't wrap
     * around an edge. Falls back to the first valid direction when it receives
     * nothing, or when no clear line reaches the next digit.
     */
    private bestSignalDirection(antenna: Antenna): Direction {
        const dirs = this.getValidSignalDirections(antenna);
        const received = this.getReceivedDigit(antenna);
        if (received === null) return dirs[0];

        const target = this.grid.nextDigit(received);
        let best: Direction | null = null;
        let bestReach: { steps: number; wrapped: boolean } | null = null;

        for (const dir of dirs) {
            const reach = this.aimReach(antenna, dir, target);
            if (!reach) continue;
            if (!bestReach ||
                reach.steps < bestReach.steps ||
                (reach.steps === bestReach.steps && bestReach.wrapped && !reach.wrapped)) {
                best = dir;
                bestReach = reach;
            }
        }
        return best ?? dirs[0];
    }

    /**
     * Follow the signal `antenna` would emit along `dir` and report how far it
     * travels to reach digit `target`, and whether it wrapped an edge on the way.
     * Null when the line doesn't cleanly arrive — another antenna blocks it, a
     * different digit sits in the way, or it loops without meeting anything.
     */
    private aimReach(
        antenna: Antenna, dir: Direction, target: number
    ): { steps: number; wrapped: boolean } | null {
        let current = { ...this.getSignalEnd(antenna) };
        const visited = new Set<string>();
        let wrapped = false;
        let steps = 0;

        while (true) {
            const prev = current;
            current = this.wrapMove(prev, dir);
            if (AntennasGame.crossesEdge(prev, current, dir)) wrapped = true;
            const teleportExit = this.checkVortexTeleport(current);
            if (teleportExit) current = teleportExit;
            steps++;

            const key = positionKey(current);
            if (visited.has(key)) return null;   // looped without arriving
            visited.add(key);

            for (const [, other] of this.antennas) {
                if (other.id === antenna.id) continue;
                if (other.path.some(cell => positionsEqual(cell, current))) return null;
            }

            const digit = this.grid.getDigitAt(current);
            if (digit !== null) {
                // The first digit met has to be the one we want; anything else is
                // in the way (a signal may only ever cross one digit).
                return digit === target ? { steps, wrapped } : null;
            }

            if (visited.size > this.grid.size * this.grid.size) return null;
        }
    }

    /** Whether a single step from `from` to `to` along `dir` wrapped a grid edge. */
    private static crossesEdge(from: Position, to: Position, dir: Direction): boolean {
        switch (dir) {
            case 'N': return to.row > from.row;
            case 'S': return to.row < from.row;
            case 'W': return to.col > from.col;
            case 'E': return to.col < from.col;
        }
    }

    cancelAntenna(): void {
        this.currentPath = [];
    }

    /**
     * Reopen a finished antenna for editing from `pos`: the antenna is removed and
     * the receiver-side part of its path — up to and including `pos` — becomes the
     * pending build, so drawing continues from there and the rest is erased.
     * Reopening at the emitter keeps the whole path; stepping back off it then
     * retracts (see {@link retractBuild}).
     */
    resumeAntenna(pos: Position): boolean {
        const antenna = this.getAntennaAtCell(pos);
        if (!antenna) return false;
        const idx = antenna.path.findIndex(p => positionsEqual(p, pos));
        if (idx < 0) return false;

        this.antennas.delete(antenna.id);
        this.currentPath = antenna.path.slice(0, idx + 1).map(p => ({ ...p }));
        return true;
    }

    /**
     * Cut the pending build back to `pos`, dropping everything drawn after it, so
     * drawing resumes from there. No-op (false) when `pos` isn't on the build.
     */
    truncateBuild(pos: Position): boolean {
        const idx = this.currentPath.findIndex(p => positionsEqual(p, pos));
        if (idx < 0) return false;
        this.currentPath = this.currentPath.slice(0, idx + 1);
        return true;
    }

    /** Drop the last cell of the pending build — tracing back over your own path
     *  erases it. Keeps at least the receiver. */
    retractBuild(): boolean {
        if (this.currentPath.length < 2) return false;
        this.currentPath.pop();
        return true;
    }

    /**
     * Handle a click on a cell. Returns true if state changed.
     * Consolidates idle and building click logic for tutorial adapter use.
     */
    handleClick(pos: Position): boolean {
        if (this.currentPath.length === 0) {
            // Idle state
            const existingAntenna = this.getAntennaAtCell(pos);

            if (existingAntenna) {
                const receiverPos = this.getFirstEnd(existingAntenna);
                const emitterPos = this.getSignalEnd(existingAntenna);

                if (positionsEqual(pos, receiverPos)) {
                    this.removeAntenna(existingAntenna.id);
                    return true;
                } else if (positionsEqual(pos, emitterPos)) {
                    this.cycleSignalDirection(existingAntenna.id);
                    return true;
                }
                return false; // Body click, no action
            }

            return this.startAntenna(pos);
        } else {
            // Building state
            if (this.isAtBuildingStart(pos)) {
                this.cancelAntenna();
                return true;
            }

            if (this.isAtBuildingEnd(pos)) {
                return this.finishAntenna();
            }

            return this.extendAntenna(pos);
        }
    }

    removeAntenna(id: number): boolean {
        return this.antennas.delete(id);
    }

    getAntennaAtCell(pos: Position): Antenna | null {
        for (const [, antenna] of this.antennas) {
            for (const cell of antenna.path) {
                if (positionsEqual(pos, cell)) {
                    return antenna;
                }
            }
        }
        return null;
    }

    // Check if position is at the building start (pending receiver)
    isAtBuildingStart(pos: Position): boolean {
        if (this.currentPath.length === 0) return false;
        return positionsEqual(pos, this.currentPath[0]);
    }

    // Check if position is at the building end (for emitter placement)
    isAtBuildingEnd(pos: Position): boolean {
        if (this.currentPath.length < 2) return false;
        return positionsEqual(pos, this.currentPath[this.currentPath.length - 1]);
    }

    // Count total digits crossed by signal (for badge display logic and emitter coloring)
    countDigitsCrossed(antenna: Antenna): number {
        const from = this.getSignalEnd(antenna);
        let current = { ...from };
        let count = 0;
        const visited = new Set<string>();

        while (true) {
            current = this.wrapMove(current, antenna.signalDirection);
            const teleportExit = this.checkVortexTeleport(current);
            if (teleportExit) current = teleportExit;

            const key = positionKey(current);
            if (visited.has(key)) break;
            visited.add(key);

            // Check for digit at this position
            for (const [, digitPos] of this.digitPositions) {
                if (positionsEqual(current, digitPos)) {
                    count++;
                    break;
                }
            }

            // Check if we hit another antenna (stop counting)
            for (const [, other] of this.antennas) {
                if (other.id !== antenna.id) {
                    for (const cell of other.path) {
                        if (positionsEqual(current, cell)) {
                            return count;
                        }
                    }
                }
            }

            if (visited.size > this.grid.size * this.grid.size) break;
        }
        return count;
    }

    /**
     * The unique digit this antenna's emitted signal crosses, or null if the
     * signal crosses zero or several digits.
     */
    getEmittedDigit(antenna: Antenna): number | null {
        const crossings = this.getSignalDigitCrossings(antenna);
        return crossings.length === 1 ? crossings[0].digit : null;
    }

    /**
     * The unique digit carried by the signal received at this antenna, or null
     * if it has no single incoming signal, or that signal crosses ≠ 1 digit.
     */
    getReceivedDigit(antenna: Antenna): number | null {
        let source: Antenna | null = null;
        for (const [, other] of this.antennas) {
            if (other.id === antenna.id) continue;
            const signal = this.getSignalForAntenna(other);
            if (signal.targetAntennaId === antenna.id) {
                if (source) return null;  // more than one incoming signal — ambiguous
                source = other;
            }
        }
        return source ? this.getEmittedDigit(source) : null;
    }

    /** Whether `b` directly follows `a` in cyclic digit order (wraps max→min). */
    private digitsConsecutive(a: number, b: number): boolean {
        return this.grid.nextDigit(a) === b;
    }

    // ── Color categories (single source of truth for the renderer) ─────

    /** Emitter: teal if its signal reaches another antenna, red if it crashes
     *  into a body, amber if it loops back without reaching anything. */
    getEmitterColor(antenna: Antenna): AntennaColor {
        const signal = this.getSignalForAntenna(antenna);
        if (signal.targetAntennaId !== null) return 'teal';
        if (signal.blocked) return 'red';
        return 'amber';
    }

    /** Receiver: teal on exactly one incoming signal, amber on none, red on 2+. */
    getReceiverColor(antenna: Antenna): AntennaColor {
        const count = this.countIncomingSignals(antenna);
        if (count === 1) return 'teal';
        if (count === 0) return 'amber';
        return 'red';
    }

    /** Body: teal when the received and emitted digits are both unique and
     *  consecutive, red when both unique but not consecutive, else amber. */
    getBodyColor(antenna: Antenna): AntennaColor {
        const inDigit = this.getReceivedDigit(antenna);
        const outDigit = this.getEmittedDigit(antenna);
        if (inDigit === null || outDigit === null) return 'amber';
        return this.digitsConsecutive(inDigit, outDigit) ? 'teal' : 'red';
    }

    /** Signal line: teal if it reaches a receiver crossing exactly one digit,
     *  red if it reaches a receiver but crosses 0 or 2+ digits, else amber. */
    getSignalColor(antenna: Antenna): AntennaColor {
        const signal = this.getSignalForAntenna(antenna);
        if (signal.targetAntennaId === null) return 'amber';
        return this.countDigitsCrossed(antenna) === 1 ? 'teal' : 'red';
    }

    /** Emitter badge: present when the emitted signal crosses a unique digit
     *  (shown). Teal/red when the receiver also has a badge and the two digits
     *  are/aren't consecutive; amber when the receiver has no badge. */
    getEmitterBadge(antenna: Antenna): BadgeInfo | null {
        const outDigit = this.getEmittedDigit(antenna);
        if (outDigit === null) return null;
        const inDigit = this.getReceivedDigit(antenna);
        const color: AntennaColor = inDigit === null
            ? 'amber'
            : this.digitsConsecutive(inDigit, outDigit) ? 'teal' : 'red';
        return { digit: outDigit, color };
    }

    /** Receiver badge: mirror of the emitter badge, keyed on the received digit. */
    getReceiverBadge(antenna: Antenna): BadgeInfo | null {
        const inDigit = this.getReceivedDigit(antenna);
        if (inDigit === null) return null;
        const outDigit = this.getEmittedDigit(antenna);
        const color: AntennaColor = outDigit === null
            ? 'amber'
            : this.digitsConsecutive(inDigit, outDigit) ? 'teal' : 'red';
        return { digit: inDigit, color };
    }

    /** Cells where a signal collides with a non-receiver antenna part (a body or
     *  emitter) — error spots to highlight. */
    getCollisionErrorCells(): Position[] {
        const cells: Position[] = [];
        for (const [, antenna] of this.antennas) {
            const signal = this.getSignalForAntenna(antenna);
            if (signal.blocked && signal.collisionPosition) {
                cells.push(signal.collisionPosition);
            }
        }
        return cells;
    }

    // Calculate signal for an antenna - traces path and checks collisions
    getSignalForAntenna(antenna: Antenna): Signal {
        const from = this.getSignalEnd(antenna);

        let current = { ...from };
        const visited = new Set<string>();
        let crossedDigit: number | null = null;

        while (true) {
            // Move one step
            current = this.wrapMove(current, antenna.signalDirection);
            const teleportExit = this.checkVortexTeleport(current);
            if (teleportExit) current = teleportExit;

            const key = positionKey(current);
            if (visited.has(key)) {
                // Full loop without finding target - signal goes nowhere valid
                return {
                    from: { ...from },
                    direction: antenna.signalDirection,
                    crossedDigit: null,
                    targetAntennaId: null,
                    blocked: false,
                    collisionPosition: null
                };
            }
            visited.add(key);

            // Check if we hit any antenna
            for (const [, other] of this.antennas) {
                if (other.id === antenna.id) continue;

                // Check if this is the first end (valid target)
                if (positionsEqual(current, this.getFirstEnd(other))) {
                    return {
                        from: { ...from },
                        direction: antenna.signalDirection,
                        crossedDigit,  // Only return digit if we reached valid target
                        targetAntennaId: other.id,
                        blocked: false,
                        collisionPosition: { ...current }
                    };
                }

                // Check if this is any part of the antenna body (blocked)
                for (const cell of other.path) {
                    if (positionsEqual(current, cell)) {
                        return {
                            from: { ...from },
                            direction: antenna.signalDirection,
                            crossedDigit: null,  // Blocked, so no valid digit crossing
                            targetAntennaId: null,
                            blocked: true,
                            collisionPosition: { ...current }
                        };
                    }
                }
            }

            // Check if this cell has a digit (only record first one found)
            if (crossedDigit === null) {
                for (const [digit, digitPos] of this.digitPositions) {
                    if (positionsEqual(current, digitPos)) {
                        crossedDigit = digit;
                        break;
                    }
                }
            }

            // Safety: don't loop forever
            if (visited.size > this.grid.size * this.grid.size) break;
        }

        return {
            from: { ...from },
            direction: antenna.signalDirection,
            crossedDigit: null,
            targetAntennaId: null,
            blocked: false,
            collisionPosition: null
        };
    }

    // Get all signals from all antennas
    getAllSignals(): Signal[] {
        const signals: Signal[] = [];
        for (const [, antenna] of this.antennas) {
            signals.push(this.getSignalForAntenna(antenna));
        }
        return signals;
    }

    // Get positions of digits that are crossed by more than one signal
    getDoubleCrossedDigits(): Position[] {
        const crossCounts = new Map<number, number>();

        for (const [, antenna] of this.antennas) {
            const digits = this.getDigitsCrossedBySignal(antenna);
            for (const digit of digits) {
                crossCounts.set(digit, (crossCounts.get(digit) || 0) + 1);
            }
        }

        const positions: Position[] = [];
        for (const [digit, count] of crossCounts) {
            if (count > 1) {
                const pos = this.digitPositions.get(digit);
                if (pos) positions.push(pos);
            }
        }
        return positions;
    }

    // Get antenna IDs whose signals cross any double-crossed digit
    getAntennasWithDoubleCrossedSignals(): Set<number> {
        const doubleCrossedPositions = this.getDoubleCrossedDigits();
        const doubleCrossedDigits = new Set<number>();
        for (const pos of doubleCrossedPositions) {
            for (const [digit, dPos] of this.digitPositions) {
                if (positionsEqual(pos, dPos)) doubleCrossedDigits.add(digit);
            }
        }

        const result = new Set<number>();
        for (const [, antenna] of this.antennas) {
            const digits = this.getDigitsCrossedBySignal(antenna);
            for (const d of digits) {
                if (doubleCrossedDigits.has(d)) {
                    result.add(antenna.id);
                    break;
                }
            }
        }
        return result;
    }

    // Count how many signals from other antennas target this antenna's receiver
    countIncomingSignals(antenna: Antenna): number {
        let count = 0;
        for (const [, other] of this.antennas) {
            if (other.id === antenna.id) continue;
            const signal = this.getSignalForAntenna(other);
            if (signal.targetAntennaId === antenna.id) count++;
        }
        return count;
    }

    // Get all digit crossings by a signal with position and direction info
    getSignalDigitCrossings(antenna: Antenna): SignalDigitCrossing[] {
        const from = this.getSignalEnd(antenna);
        let current = { ...from };
        const crossings: SignalDigitCrossing[] = [];
        const visited = new Set<string>();

        while (true) {
            current = this.wrapMove(current, antenna.signalDirection);
            const teleportExit = this.checkVortexTeleport(current);
            if (teleportExit) current = teleportExit;

            const key = positionKey(current);
            if (visited.has(key)) break;
            visited.add(key);

            // Check for digit at this position
            for (const [digit, digitPos] of this.digitPositions) {
                if (positionsEqual(current, digitPos)) {
                    crossings.push({
                        position: { ...current },
                        digit,
                        direction: antenna.signalDirection
                    });
                    break;
                }
            }

            // Check if we hit another antenna (stop)
            let hitAntenna = false;
            for (const [, other] of this.antennas) {
                if (other.id !== antenna.id) {
                    for (const cell of other.path) {
                        if (positionsEqual(current, cell)) {
                            hitAntenna = true;
                            break;
                        }
                    }
                }
                if (hitAntenna) break;
            }
            if (hitAntenna) break;

            if (visited.size > this.grid.size * this.grid.size) break;
        }
        return crossings;
    }

    // Get all digits crossed by a signal (not just the first one)
    private getDigitsCrossedBySignal(antenna: Antenna): number[] {
        return this.getSignalDigitCrossings(antenna).map(c => c.digit);
    }

    // Try to form valid cycle - check if antennas connect properly
    tryFormCycle(): { valid: boolean; clearedDigits: number[] } {
        if (this.antennas.size !== this.grid.digitCount) {
            return { valid: false, clearedDigits: [] };
        }

        const antennaList = Array.from(this.antennas.values());

        // Build signal map: antenna id -> signal info
        const signalMap = new Map<number, Signal>();
        for (const antenna of antennaList) {
            signalMap.set(antenna.id, this.getSignalForAntenna(antenna));
        }

        // Try to find a valid ordering starting from each antenna
        for (const startAntenna of antennaList) {
            const result = this.tryCycleFromAntenna(startAntenna, antennaList, signalMap);
            if (result.valid) {
                return result;
            }
        }

        return { valid: false, clearedDigits: [] };
    }

    private tryCycleFromAntenna(
        start: Antenna,
        allAntennas: Antenna[],
        signalMap: Map<number, Signal>
    ): { valid: boolean; clearedDigits: number[] } {
        const visited = new Set<number>();
        const clearedDigits: number[] = [];
        let current = start;
        let expectedDigit = this.grid.minDigit;

        while (visited.size < allAntennas.length) {
            visited.add(current.id);

            const signal = signalMap.get(current.id);
            if (!signal || signal.blocked || signal.targetAntennaId === null) {
                return { valid: false, clearedDigits: [] };
            }

            // Signal must cross the expected digit
            if (signal.crossedDigit !== expectedDigit) {
                return { valid: false, clearedDigits: [] };
            }
            clearedDigits.push(expectedDigit);
            expectedDigit++;

            // Get next antenna
            const nextAntenna = this.antennas.get(signal.targetAntennaId);
            if (!nextAntenna) return { valid: false, clearedDigits: [] };

            // Check if we've completed the cycle
            if (nextAntenna.id === start.id) {
                if (visited.size === allAntennas.length && clearedDigits.length === this.grid.digitCount) {
                    return { valid: true, clearedDigits };
                }
                return { valid: false, clearedDigits: [] };
            }

            if (visited.has(nextAntenna.id)) {
                return { valid: false, clearedDigits: [] };
            }

            current = nextAntenna;
        }

        return { valid: false, clearedDigits: [] };
    }

    getClearedDigits(): number[] {
        const result = this.tryFormCycle();
        return result.clearedDigits;
    }

    checkWin(): boolean {
        const result = this.tryFormCycle();
        return result.valid && result.clearedDigits.length === this.grid.digitCount;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        // A = total antenna cells
        let antennaCells = 0;
        for (const [, antenna] of this.antennas) {
            antennaCells += antenna.path.length;
        }

        // B = empty cells visited by signals
        const signalCells = this.getSignalCells();

        return calculateScore(antennaCells, signalCells.length);
    }

    /** Empty (non-digit) cells traversed by every antenna's signal, until it loops,
     *  hits another antenna, or fills the grid. Used by scoring and its overlay. */
    getSignalCells(): Position[] {
        const cells = new Map<string, Position>();
        for (const [, antenna] of this.antennas) {
            const from = this.getSignalEnd(antenna);
            let current = { ...from };
            const visited = new Set<string>();

            while (true) {
                current = this.wrapMove(current, antenna.signalDirection);
                const teleportExit = this.checkVortexTeleport(current);
                if (teleportExit) current = teleportExit;

                const key = positionKey(current);
                if (visited.has(key)) break;
                visited.add(key);

                // Stop if we hit another antenna.
                let hitAntenna = false;
                for (const [, other] of this.antennas) {
                    if (other.id === antenna.id) continue;
                    if (other.path.some(cell => positionsEqual(current, cell))) { hitAntenna = true; break; }
                }
                if (hitAntenna) break;

                let isDigit = false;
                for (const [, digitPos] of this.digitPositions) {
                    if (positionsEqual(current, digitPos)) { isDigit = true; break; }
                }
                if (!isDigit) cells.set(key, { ...current });

                if (visited.size > this.grid.size * this.grid.size) break;
            }
        }
        return [...cells.values()];
    }

    /** Scoring-rules overlay: signal-visited empty cells (secondary). Antennas
     *  (primary) are redrawn in amber by the renderer. */
    getScoringHighlight(): ScoringHighlight {
        return { secondaryCells: this.getSignalCells() };
    }

    reset(): void {
        this.antennas.clear();
        this.currentPath = [];
        this.nextAntennaId = 1;
    }

    /**
     * Jumpstart a solution: place the two antennas around digit 1. The "before"
     * antenna (A) sits beside digit 1 and fires its signal through it into the
     * "after" antenna's receiver, just past digit 1 — showing how a signal
     * carries a digit to the next antenna. Where possible, the "after" antenna
     * (B) then bends so its own emitter points across digit 2 (the 1→2 link).
     * Falls back to a minimal two-antenna link. Verified against the signal sim.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const min = this.grid.minDigit;
        const d1 = this.digitPositions.get(min);
        if (!d1) return false;
        const d2 = this.digitPositions.get(min + 1);

        const r1 = d1.row, c1 = d1.col;
        const size = this.grid.size;
        const free = (r: number, c: number) =>
            r >= 0 && r < size && c >= 0 && c < size && !this.grid.hasDigitAt({ row: r, col: c });
        const P = (r: number, c: number): Position => ({ row: r, col: c });

        // A fires through digit 1 along its row/column into B's receiver (bRecv).
        const lines = [
            { aRecv: P(r1, c1 - 2), aEmit: P(r1, c1 - 1), aDir: 'E' as Direction, bRecv: P(r1, c1 + 1) },
            { aRecv: P(r1, c1 + 2), aEmit: P(r1, c1 + 1), aDir: 'W' as Direction, bRecv: P(r1, c1 - 1) },
            { aRecv: P(r1 - 2, c1), aEmit: P(r1 - 1, c1), aDir: 'S' as Direction, bRecv: P(r1 + 1, c1) },
            { aRecv: P(r1 + 2, c1), aEmit: P(r1 + 1, c1), aDir: 'N' as Direction, bRecv: P(r1 - 1, c1) },
        ];

        // Phase 1: B's emitter aimed across digit 2 (the 1→2 showcase).
        if (d2) {
            for (const ln of lines) {
                if (![ln.aRecv, ln.aEmit, ln.bRecv].every(p => free(p.row, p.col))) continue;
                const occupied = new Set([ln.aRecv, ln.aEmit].map(p => positionKey(p)));
                for (const b of this.buildBPathsToDigit2(ln.bRecv, d2, occupied, free)) {
                    if (this.tryTwoAntennas(min, [ln.aRecv, ln.aEmit], ln.aDir, b.path, b.dir, true)) return true;
                }
            }
        }

        // Phase 2: minimal link — B just receives the digit-1 signal.
        for (const ln of lines) {
            const bEmit = P(ln.bRecv.row + (ln.aDir === 'E' || ln.aDir === 'W' ? 0 : (ln.aDir === 'S' ? 1 : -1)),
                ln.bRecv.col + (ln.aDir === 'E' ? 1 : ln.aDir === 'W' ? -1 : 0));
            if (![ln.aRecv, ln.aEmit, ln.bRecv, bEmit].every(p => free(p.row, p.col))) continue;
            const bDir: Direction = (ln.aDir === 'E' || ln.aDir === 'W') ? 'N' : 'E';
            if (this.tryTwoAntennas(min, [ln.aRecv, ln.aEmit], ln.aDir, [ln.bRecv, bEmit], bDir, false)) return true;
        }

        this.reset();
        return false;
    }

    /** Place A and B; verify A's signal crosses digit 1 into B (and optionally B crosses digit 2). */
    private tryTwoAntennas(
        min: number, aPath: Position[], aDir: Direction, bPath: Position[], bDir: Direction, requireB2: boolean
    ): boolean {
        this.reset();
        const aId = this.nextAntennaId++;
        this.antennas.set(aId, { id: aId, path: aPath.map(p => ({ ...p })), signalDirection: aDir });
        const bId = this.nextAntennaId++;
        const b: Antenna = { id: bId, path: bPath.map(p => ({ ...p })), signalDirection: bDir };
        if (!this.getValidSignalDirections(b).includes(bDir)) return false;
        this.antennas.set(bId, b);

        const a = this.antennas.get(aId)!;
        const sig = this.getSignalForAntenna(a);
        if (sig.crossedDigit !== min || sig.targetAntennaId !== bId || this.countDigitsCrossed(a) !== 1) return false;

        if (requireB2) {
            const crossings = this.getSignalDigitCrossings(b);
            if (crossings.length === 0 || crossings[0].digit !== min + 1) return false;
        }
        return true;
    }

    /**
     * Candidate L-paths for the "after" antenna: receiver at bRecv, bending to an
     * emitter placed next to digit 2 and firing across it.
     */
    private buildBPathsToDigit2(
        bRecv: Position, d2: Position, occupied: Set<string>, free: (r: number, c: number) => boolean
    ): Array<{ path: Position[]; dir: Direction }> {
        const out: Array<{ path: Position[]; dir: Direction }> = [];
        const emitters: Array<{ pos: Position; dir: Direction }> = [
            { pos: { row: d2.row, col: d2.col - 1 }, dir: 'E' },
            { pos: { row: d2.row, col: d2.col + 1 }, dir: 'W' },
            { pos: { row: d2.row - 1, col: d2.col }, dir: 'S' },
            { pos: { row: d2.row + 1, col: d2.col }, dir: 'N' },
        ];
        for (const e of emitters) {
            if (!free(e.pos.row, e.pos.col)) continue;
            for (const corner of [{ row: bRecv.row, col: e.pos.col }, { row: e.pos.row, col: bRecv.col }]) {
                const path = this.expandLPath(bRecv, corner, e.pos);
                if (!path) continue;
                if (path.some((p, i) => i > 0 && occupied.has(positionKey(p)))) continue;
                if (path.some(p => !free(p.row, p.col))) continue;
                out.push({ path, dir: e.dir });
            }
        }
        return out;
    }

    /** Expand a 3-waypoint L (start→corner→end, axis-aligned) into a cell path, or null if degenerate. */
    private expandLPath(start: Position, corner: Position, end: Position): Position[] | null {
        const seg = (a: Position, b: Position): Position[] | null => {
            if (a.row !== b.row && a.col !== b.col) return null;
            const cells: Position[] = [];
            const dr = Math.sign(b.row - a.row), dc = Math.sign(b.col - a.col);
            let cur = { ...a };
            while (cur.row !== b.row || cur.col !== b.col) { cur = { row: cur.row + dr, col: cur.col + dc }; cells.push({ ...cur }); }
            return cells;
        };
        const s1 = seg(start, corner), s2 = seg(corner, end);
        if (!s1 || !s2) return null;
        const path = [start, ...s1, ...s2];
        const seen = new Set<string>();
        for (const p of path) { const k = positionKey(p); if (seen.has(k)) return null; seen.add(k); }
        return path.length >= 2 ? path : null;
    }

    getStatus(): string {
        const boldRed = (text: string) =>
            `<span style="color:#e94560;font-weight:bold">${text}</span>`;

        if (this.checkWin()) {
            return `You win! All antennas connected in a cycle through digits ${this.grid.minDigit}-${this.grid.maxDigit}!`;
        }

        if (this.currentPath.length > 0) {
            if (this.currentPath.length < 2) {
                return `Building antenna: click adjacent cells to extend.`;
            }
            return `Building antenna: ${this.currentPath.length} cells. Click end to finish.`;
        }

        const antennaCount = this.antennas.size;
        const total = this.grid.digitCount;
        const min = this.grid.minDigit, max = this.grid.maxDigit;

        if (antennaCount === 0) {
            return 'Click an empty cell to start an antenna, then extend it and click the end to finish.';
        }

        // ── Red conditions take priority, highest first ──
        if (this.getDoubleCrossedDigits().length > 0) {
            return `${boldRed('Two signals cross the same digit')} — reroute one of them.`;
        }

        // Classify the red signal / body conditions in a single pass.
        let crossesTwo = false, crossesNone = false, badBody = false;
        for (const [, antenna] of this.antennas) {
            if (this.getSignalColor(antenna) === 'red') {
                if (this.countDigitsCrossed(antenna) >= 2) crossesTwo = true;
                else crossesNone = true;
            }
            if (this.getBodyColor(antenna) === 'red') badBody = true;
        }

        if (crossesTwo) {
            return `${boldRed('A signal crosses more than one digit')} — each must cross exactly one.`;
        }

        if (this.getCollisionErrorCells().length > 0) {
            return `${boldRed('A signal crashes into an antenna')} — only a receiver end may be hit.`;
        }

        for (const [, antenna] of this.antennas) {
            if (this.countIncomingSignals(antenna) >= 2) {
                return `${boldRed('A receiver is hit by multiple signals')} — re-aim one of them.`;
            }
        }

        if (crossesNone) {
            return `${boldRed('A signal reaches an antenna without crossing a digit')} — route it across one.`;
        }

        if (badBody) {
            return `${boldRed('Linked antennas are out of order')} — each signal must carry the next digit up.`;
        }

        // ── No red: guide the next step ──
        if (antennaCount < total) {
            return `Placed ${antennaCount}/${total} antennas. Add the rest, sending each signal across one digit into another antenna.`;
        }

        const looping = [...this.antennas.values()]
            .filter(a => this.getEmitterColor(a) === 'amber').length;
        if (looping > 0) {
            return `${looping} signal${looping > 1 ? 's' : ''} loop without reaching an antenna — re-aim each emitter at a receiver.`;
        }

        return `Connect every antenna into a single ${min}→${max} loop.`;
    }

    /**
     * Serialize game state to string format.
     * Format: "A0A1A2E,B3B4N" - comma-separated antennas,
     * each antenna is positions followed by signal direction.
     */
    serialize(): string {
        const entries: string[] = [];
        for (const [, antenna] of this.antennas) {
            const posStrs = antenna.path.map(p => this.gs.serializePosition(p)).join('');
            entries.push(`${posStrs}${antenna.signalDirection}`);
        }
        return entries.join('');
    }

    /**
     * Deserialize game state from string format.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        const compact = stripSeparators(serialized);
        const re = /([A-J]\d){2,}[NSEW]/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(compact)) !== null) {
            const token = m[0];
            const dirChar = token[token.length - 1];
            if (!['N', 'E', 'S', 'W'].includes(dirChar)) continue;
            const direction = dirChar as Direction;

            const posStr = token.slice(0, -1);
            const path: Position[] = [];
            for (let i = 0; i < posStr.length; i += 2) {
                try {
                    path.push(this.gs.deserializePosition(posStr.slice(i, i + 2)));
                } catch {
                    break;
                }
            }
            if (path.length < 2) continue;

            const antenna: Antenna = {
                id: this.nextAntennaId,
                path,
                signalDirection: direction
            };
            this.antennas.set(antenna.id, antenna);
            this.nextAntennaId++;
        }
    }
}
